import {
  isAllowedEmail,
  normalizeEmail,
  resolveRoleFromSignInCount,
} from '@/config/authAllowlist';
import { AUTH_SESSION_VERSION, STORAGE_KEYS } from '@/utils/constants';
import type { User, UserRole } from '@/types';

const DEFAULT_OPERATOR_SITE = '3rd, 9th & 10th floors';

function readSignInCounts(): Record<string, number> {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.SIGN_IN_COUNTS);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, number>;
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function writeSignInCounts(counts: Record<string, number>): void {
  localStorage.setItem(STORAGE_KEYS.SIGN_IN_COUNTS, JSON.stringify(counts));
}

function initialsFromName(firstName: string, lastName: string): string {
  const a = firstName.charAt(0)?.toUpperCase() ?? '';
  const b = lastName.charAt(0)?.toUpperCase() ?? '';
  return `${a}${b}` || 'U';
}

function isStoredSessionValid(email: string, firstName: string, lastName: string): boolean {
  if (!email || !isAllowedEmail(email)) return false;
  if (!firstName.trim() || !lastName.trim()) return false;
  return true;
}

/** Drop pre-login mock sessions (auto-seeded default user, etc.). */
function migrateSessionVersion(): void {
  const stored = localStorage.getItem(STORAGE_KEYS.AUTH_SESSION_VERSION);
  if (stored === AUTH_SESSION_VERSION) return;

  localStorage.removeItem(STORAGE_KEYS.USER_ID);
  localStorage.removeItem(STORAGE_KEYS.USER_EMAIL);
  localStorage.removeItem(STORAGE_KEYS.USER_NAME);
  localStorage.removeItem(STORAGE_KEYS.USER_FIRST_NAME);
  localStorage.removeItem(STORAGE_KEYS.USER_LAST_NAME);
  localStorage.removeItem(STORAGE_KEYS.USER_ROLE);
  localStorage.removeItem(STORAGE_KEYS.USER_SITE);
  localStorage.setItem(STORAGE_KEYS.AUTH_SESSION_VERSION, AUTH_SESSION_VERSION);
}

export const authService = {
  /** Call once on app load before reading the session. */
  bootstrap(): User | null {
    migrateSessionVersion();
    return this.getUser();
  },

  incrementSignInCount(email: string): number {
    const key = normalizeEmail(email);
    const counts = readSignInCounts();
    const next = (counts[key] ?? 0) + 1;
    counts[key] = next;
    writeSignInCounts(counts);
    return next;
  },

  getSignInCount(email: string): number {
    const key = normalizeEmail(email);
    return readSignInCounts()[key] ?? 0;
  },

  resolveRoleFromSignInCount(count: number): UserRole {
    return resolveRoleFromSignInCount(count);
  },

  setUser(user: User): void {
    localStorage.setItem(STORAGE_KEYS.AUTH_SESSION_VERSION, AUTH_SESSION_VERSION);
    localStorage.setItem(STORAGE_KEYS.USER_ID, user.id);
    localStorage.setItem(STORAGE_KEYS.USER_EMAIL, user.email);
    localStorage.setItem(STORAGE_KEYS.USER_NAME, user.name);
    localStorage.setItem(STORAGE_KEYS.USER_FIRST_NAME, user.firstName);
    localStorage.setItem(STORAGE_KEYS.USER_LAST_NAME, user.lastName);
    localStorage.setItem(STORAGE_KEYS.USER_ROLE, user.role);
    localStorage.setItem(STORAGE_KEYS.USER_SITE, user.site);
  },

  getUser(): User | null {
    migrateSessionVersion();

    const id = localStorage.getItem(STORAGE_KEYS.USER_ID);
    if (!id) return null;

    const email = localStorage.getItem(STORAGE_KEYS.USER_EMAIL) ?? '';
    const firstName = localStorage.getItem(STORAGE_KEYS.USER_FIRST_NAME) ?? '';
    const lastName = localStorage.getItem(STORAGE_KEYS.USER_LAST_NAME) ?? '';

    if (!isStoredSessionValid(email, firstName, lastName)) {
      this.clearUser();
      return null;
    }

    const name =
      localStorage.getItem(STORAGE_KEYS.USER_NAME) ??
      (`${firstName} ${lastName}`.trim() || 'User');
    const role = (localStorage.getItem(STORAGE_KEYS.USER_ROLE) ?? 'novice') as User['role'];
    const site = localStorage.getItem(STORAGE_KEYS.USER_SITE) ?? DEFAULT_OPERATOR_SITE;
    const initials = initialsFromName(firstName, lastName);

    return { id, email, name, firstName, lastName, role, site, initials };
  },

  clearUser(): void {
    localStorage.removeItem(STORAGE_KEYS.USER_ID);
    localStorage.removeItem(STORAGE_KEYS.USER_EMAIL);
    localStorage.removeItem(STORAGE_KEYS.USER_NAME);
    localStorage.removeItem(STORAGE_KEYS.USER_FIRST_NAME);
    localStorage.removeItem(STORAGE_KEYS.USER_LAST_NAME);
    localStorage.removeItem(STORAGE_KEYS.USER_ROLE);
    localStorage.removeItem(STORAGE_KEYS.USER_SITE);
  },

  isAuthenticated(): boolean {
    return this.getUser() !== null;
  },
};
